import indeedLogo from "./IndeedLogo.svg";
import successfulSignInImage from "./SuccessfulSignInImage.png";
import { Box, Pressable, Image, Modal, Text, type TextRef } from "../../../../../../mads";
import { useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import { markPostApplyAdPending } from "../../PostApplyAd/postApplyAdPending";

import {
  AvroOptional,
  log as logReceivedFlag,
} from "@indeed/signals-schema/com/indeed/signals/schema/profile/MobRcvFlag";
import { useCallback, useEffect, useRef, type FC } from "react";
import { StyleSheet } from "react-native";

const logModalDisplayed = (mobtk: string, src: string) => {
  logReceivedFlag({
    mobtk,
    parentEventType: "mobviewjob",
    flag: AvroOptional.string(`${src}_MODAL_SEEN`),
    ts: AvroOptional.long(new Date().getTime()),
  });
};

const logCreateAccountSuccessContinue = (mobtk: string, src: string) => {
  logReceivedFlag({
    mobtk,
    parentEventType: "mobviewjob",
    flag: AvroOptional.string(`${src}_CREATE_ACCOUNT_SUCCESS_CONTINUE`),
    ts: AvroOptional.long(new Date().getTime()),
  });
};

// Match the jobseeker-frontend (React) flag naming so RN web and React web share
// the same registered ReceivedFlagType values / dashboards. React splits these
// by surface: modal-seen uses DESKTOP_SUCCESSFUL_SIGNIN / MOBILE_SUCCESSFUL_SIGNIN,
// the continue event uses DESKTOP / MOBILE (see frontendLoggingUtil in React).
const MODAL_SEEN_SRC_DESKTOP = "DESKTOP_SUCCESSFUL_SIGNIN";
const MODAL_SEEN_SRC_MOBILE = "MOBILE_SUCCESSFUL_SIGNIN";
const CONTINUE_SRC_DESKTOP = "DESKTOP";
const CONTINUE_SRC_MOBILE = "MOBILE";

export interface SuccessfulSignInModalProps {
  isOpen: boolean;
  onClose?: () => void;
  titleText?: string;
  successfulSigninText?: string;
  continueButtonText?: string;
  thirdPartyUrl: string | null;
  mobtk?: string;
  jobKey?: string;
}

export const SuccessfulSignInModal: FC<SuccessfulSignInModalProps> = ({
  isOpen,
  onClose,
  titleText = "Success!",
  successfulSigninText = "You've signed in to your account. Good luck with your application.",
  continueButtonText = "Continue to application",
  thirdPartyUrl,
  mobtk,
  jobKey,
}) => {
  const titleRef = useRef<TextRef>(null);
  const isDesktop = useIsDesktop();
  const modalSeenSrc = isDesktop ? MODAL_SEEN_SRC_DESKTOP : MODAL_SEEN_SRC_MOBILE;
  const continueSrc = isDesktop ? CONTINUE_SRC_DESKTOP : CONTINUE_SRC_MOBILE;
  useEffect(() => {
    if (isOpen) {
      if (mobtk) {
        logModalDisplayed(mobtk, modalSeenSrc);
      }
      // Focus title for accessibility
      titleRef.current?.focus?.();
    }
  }, [isOpen, mobtk, modalSeenSrc]);

  const handleRedirectToThirdPartySite = useCallback(() => {
    if (mobtk) {
      logCreateAccountSuccessContinue(mobtk, continueSrc);
    }

    // Log apply-authed-redirect
    if (mobtk) {
      const thirdPartyApplyLogUrl = new URL(
        "/m/rpc/log/apply-authed-redirect",
        window.location.origin,
      );
      thirdPartyApplyLogUrl.searchParams.set("tk", mobtk);
      void fetch(thirdPartyApplyLogUrl.toString());
    }

    if (thirdPartyUrl) {
      // Append log=2 to mark this as the post-passport-sign-in completion of an
      // intercepted apply (distinguishes from a normal /applystart redirect, which
      // has no log param, and from the at-gate-time intercept pixel which uses log=1).
      const redirectUrl = `${thirdPartyUrl}${thirdPartyUrl.includes("?") ? "&" : "?"}log=2`;
      // Record pending 3P-apply intent BEFORE the new tab opens so PostApplyAd's
      // tab-return listener can fire when the user comes back.
      if (jobKey && mobtk) {
        markPostApplyAdPending({ jobKey, mobvjtk: mobtk });
      }
      window.open(redirectUrl, "_blank", "noopener,noreferrer");
    }

    onClose?.();
  }, [thirdPartyUrl, mobtk, jobKey, onClose, continueSrc]);

  const handleExit = useCallback(() => {
    onClose?.();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={handleExit}
      statusBarTranslucent
    >
      <Box sx={styles.fullScreenContainer}>
        <Box sx={styles.header}>
          <Image source={indeedLogo} style={styles.logo} accessible={false} />
        </Box>

        <Box sx={styles.content}>
          <Image source={successfulSignInImage} style={styles.image} accessible={false} />
          <Text ref={titleRef} sx={styles.title} accessibilityRole="header">
            {titleText}
          </Text>

          <Text sx={styles.text}>{successfulSigninText}</Text>

          {thirdPartyUrl && (
            <Pressable
              onPress={handleRedirectToThirdPartySite}
              accessibilityRole="link"
              accessibilityLabel={continueButtonText}
            >
              <Text sx={styles.continueLink}>{continueButtonText}</Text>
            </Pressable>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  header: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
  },
  logo: {
    height: 30,
    width: 120,
    resizeMode: "contain",
  },
  content: {
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  image: {
    width: "90%",
    maxWidth: 349,
    aspectRatio: 349 / 233,
    marginTop: 20,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    color: "#2d2d2d",
    textAlign: "center",
    marginBottom: 8,
  },
  text: {
    fontSize: 16,
    color: "#595959",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 24,
  },
  continueLink: {
    color: "#2d2d2d",
    fontSize: 16,
    fontWeight: "700",
  },
});
